export interface AttachmentTagSnapshot {
	id: string;
	name: string;
	description?: string | null;
	allowedKind: string;
	minimumCount: number;
	sortOrder: number;
}

export interface AttachmentTagDraft {
	/** Existing tag id, or a client-only id for a new row. */
	key: string;
	persistedId?: string;
	name: string;
	description: string;
	allowedKind: string;
	minimumCount: number;
}

export type AttachmentTagSaveOp =
	| {
			type: "create";
			draft: AttachmentTagDraft;
			sortOrder: number;
	  }
	| {
			type: "update";
			id: string;
			draft: AttachmentTagDraft;
			sortOrder: number;
	  }
	| { type: "delete"; id: string };

function sameText(a: string | null | undefined, b: string): boolean {
	return (a ?? "") === b;
}

export function diffAttachmentTags(
	existing: AttachmentTagSnapshot[],
	drafts: AttachmentTagDraft[],
): AttachmentTagSaveOp[] {
	const ops: AttachmentTagSaveOp[] = [];
	const keptIds = new Set<string>();

	drafts.forEach((draft, index) => {
		const sortOrder = index + 1;
		if (!draft.persistedId) {
			ops.push({ type: "create", draft, sortOrder });
			return;
		}
		keptIds.add(draft.persistedId);
		const previous = existing.find((tag) => tag.id === draft.persistedId);
		if (!previous) {
			ops.push({ type: "create", draft, sortOrder });
			return;
		}
		const changed =
			previous.name !== draft.name ||
			!sameText(previous.description, draft.description) ||
			previous.allowedKind !== draft.allowedKind ||
			previous.minimumCount !== draft.minimumCount ||
			previous.sortOrder !== sortOrder;
		if (changed) {
			ops.push({
				type: "update",
				id: draft.persistedId,
				draft,
				sortOrder,
			});
		}
	});

	for (const tag of existing) {
		if (!keptIds.has(tag.id)) {
			ops.push({ type: "delete", id: tag.id });
		}
	}

	return ops;
}

export function attachmentTagsSaveEnabled(drafts: AttachmentTagDraft[]): boolean {
	if (drafts.length === 0) return false;
	return drafts.every(
		(draft) =>
			draft.name.trim().length > 0 &&
			draft.name.trim().length <= 50 &&
			draft.description.length <= 2000 &&
			draft.minimumCount >= 1 &&
			(draft.allowedKind === "photo" ||
				draft.allowedKind === "file" ||
				draft.allowedKind === "any"),
	);
}
